/**
 * OtpForm.tsx
 *
 * 6-digit OTP entry form with individual cell inputs.
 * Extracted from example/mvp/login.html (.login__code / signup__code-cell).
 *
 * Features
 * --------
 * - Auto-advance: typing a digit moves focus to the next cell.
 * - Backspace: clears current cell and moves focus to the previous cell.
 * - Paste: pastes up to 6 digits across all cells.
 * - Resend: 30-second cooldown timer, then enables a "Resend code" button.
 * - Error banner, Back button, alternate method link.
 *
 * PROPS
 * -----
 * onSubmit(code: string)        — called with the 6-digit string
 * onResend()                    — called when "Resend code" is clicked
 * onBack()                      — back button handler
 * target                        — display string ("your email" / "+1 555 …")
 * isLoading                     — disables verify button during API call
 * errorMessage                  — OTP incorrect banner text
 * alternateLabel                — e.g. "Prefer to use your password?"
 * onAlternate()                 — switch-to-password handler
 * resendCooldownSeconds         — default 30
 * submitLabel                   — button label, default "Log in"
 *
 * Auth0 / ACUL note
 * -----------------
 * On submit, the parent calls `loginPasswordlessEmailCode.continueWithCode()`
 * or `loginPasswordlessSmsOtp.continueWithCode()` with the assembled code.
 */

import { useEffect, useId, useRef, useState } from "react";

import { BackButton } from "./BackButton";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OtpFormProps {
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack?: () => void;
  target?: string;
  isLoading?: boolean;
  errorMessage?: string;
  alternateLabel?: string;
  onAlternate?: () => void;
  resendCooldownSeconds?: number;
  submitLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CELLS = 6;

export function OtpForm({
  onSubmit,
  onResend,
  onBack,
  target,
  isLoading = false,
  errorMessage,
  alternateLabel,
  onAlternate,
  resendCooldownSeconds = 30,
  submitLabel = "Log in",
}: OtpFormProps) {
  const uid = useId();
  const [digits, setDigits] = useState<string[]>(Array(CELLS).fill(""));
  const [countdown, setCountdown] = useState(resendCooldownSeconds);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleResend = () => {
    setCountdown(resendCooldownSeconds);
    setDigits(Array(CELLS).fill(""));
    refs.current[0]?.focus();
    onResend();
  };

  const code = digits.join("");
  const canSubmit = !isLoading && code.length === CELLS;

  const focusCell = (index: number) => {
    refs.current[Math.max(0, Math.min(CELLS - 1, index))]?.focus();
  };

  const updateDigit = (index: number, value: string) => {
    const next = [...digits];
    next[index] = value;
    setDigits(next);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      updateDigit(index, "");
      return;
    }
    const digit = raw.slice(-1); // take last digit typed
    updateDigit(index, digit);
    e.target.dataset.filled = "true";
    if (index < CELLS - 1) focusCell(index + 1);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        updateDigit(index, "");
        const el = refs.current[index];
        if (el) el.dataset.filled = "";
      } else {
        focusCell(index - 1);
      }
    } else if (e.key === "ArrowLeft") {
      focusCell(index - 1);
    } else if (e.key === "ArrowRight") {
      focusCell(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CELLS);
    const next = Array(CELLS).fill("");
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setDigits(next);
    focusCell(Math.min(pasted.length, CELLS - 1));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(code);
  };

  return (
    <form className="auth-view" onSubmit={handleSubmit} noValidate>
      {/* Context prompt */}
      {target && (
        <p className="auth-code-prompt">
          We sent a 6-digit code to{" "}
          <span className="auth-code-target">{target}</span>.
        </p>
      )}

      {/* 6 cells */}
      <div
        className="auth-code-grid"
        role="group"
        aria-label="Verification code"
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            id={`${uid}-cell-${i}`}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            className="auth-code-cell"
            data-filled={digit ? "true" : undefined}
            value={digit}
            aria-label={`Digit ${i + 1}`}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onPaste={handlePaste}
          />
        ))}
      </div>

      {/* Auth0 error */}
      {errorMessage && (
        <div className="auth-error-banner" role="alert">
          {errorMessage}
        </div>
      )}

      {/* Alternate method switch */}
      {alternateLabel && onAlternate && (
        <p className="auth-mode-switch">
          <button type="button" onClick={onAlternate}>
            {alternateLabel}
          </button>
        </p>
      )}

      {/* Resend row */}
      <div className="auth-code-actions">
        <span className="auth-code-actions-text">
          {countdown > 0
            ? `Resend available in ${countdown} second${countdown !== 1 ? "s" : ""}.`
            : "Didn\u2019t receive the code?"}
        </span>
        <button
          type="button"
          className="auth-resend-btn"
          disabled={countdown > 0}
          onClick={handleResend}
        >
          Resend code
        </button>
      </div>

      {/* Submit + back */}
      <div className="auth-submit-block">
        <button type="submit" className="auth-btn" disabled={!canSubmit}>
          {isLoading ? "Verifying…" : submitLabel}
        </button>

        {onBack && (
          <BackButton onClick={onBack}>
            Use a different email or phone
          </BackButton>
        )}
      </div>
    </form>
  );
}

export default OtpForm;
